import { useCallback } from 'react';

import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';
import { useLocale } from '#src/app/shared/hooks/l10n';

import { useCanReportComment } from './useCanReportComment';

type Comment = { __typename: 'PetitionComment' | 'PetitionVideoComment'; id: string; user: { id: string } };

type Result = ModelHookResult<
	{ canReportComment: boolean },
	{
		report: () => void;
	}
>;

export function useReportComment({ petitionId, comment }: { petitionId: string; comment: Comment }): Result {
	const { track } = useCampaignTracking();
	const locale = useLocale();
	const allowedLocales = ['en-US', 'en-GB'];

	const { canReportComment, currentUser } = useCanReportComment(comment);

	const report = useCallback(() => {
		let payload: TrackingProperties = {
			petition_id: petitionId,
			reporter_id: currentUser?.id,
		};
		payload =
			comment.__typename === 'PetitionVideoComment'
				? { ...payload, video_id: comment.id, video_creator_id: comment.user.id }
				: { ...payload, comment_id: comment.id, comment_creator_id: comment.user.id };

		track('supporter_voice_report_button_click', payload);
	}, [currentUser, petitionId, track, comment]);

	return {
		data: { canReportComment: canReportComment && allowedLocales.includes(locale) },
		actions: {
			report,
		},
	};
}
